import { useMemo } from 'react';
import { useQuery } from 'urql';

import { removeBadSkills } from '../utils/removeBadSkills';

export type SpecificCustomSkill = {
  tax_id: number;
  tax_name: string;
  tax_version: string;
  refs: Array<{
    id: number;
    short_name: string;
    long_name: string;
    titles: string[];
    parents: number[];
  }>;
  label: {
    level_id: number;
    tag: string;
    label: string;
  };
};

const specificCustomSkillsQueryWrapper = (levelQuery: string) =>
  levelQuery
    ? `
query SpecificCustomSkills {
  selectionList {${levelQuery}
  }
}`
    : '';

const constructALevel = ({
  levelId,
  taxonomyId,
}: {
  levelId: number;
  taxonomyId: number;
}) => `
    skill_${levelId}: skill_taxonomy(
      taxonomy_select: { taxonomy_id: ${taxonomyId}, level_id: ${levelId} }
    ) {
      tax_id
      tax_name
      tax_version
      refs {
        id
        short_name
        long_name
        titles
        parents
      }
      label {
        level_id
        tag
        label
      }
    }`;

export const useSpecificCustomSkills = ({
  levelIds,
  taxonomyId,
}: {
  levelIds: number[];
  taxonomyId: number;
}) => {
  const levelQueries = levelIds.map((levelId) =>
    constructALevel({ levelId, taxonomyId })
  );
  const query = specificCustomSkillsQueryWrapper(levelQueries.join(''));

  const [{ data, ...useQueryRest }] = useQuery({
    query,
    pause: levelIds.length === 0,
  });

  const d = useMemo<SpecificCustomSkill[]>(() => {
    const selectionList = data?.selectionList ?? {};
    return Object.values<SpecificCustomSkill>(selectionList).reduce<
      SpecificCustomSkill[]
    >((acc, curr) => {
      const refs = removeBadSkills(curr?.refs);
      if (refs.length === 0) return acc;
      return [...acc, { ...curr, refs }];
    }, []);
  }, [data]);

  return {
    data: d,
    ...useQueryRest,
  };
};
